import { computed, DestroyRef, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EventBusService } from '@shared';
import { interval } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { CLOCK_EVENTS, ClockTimeEvent } from '../components/app-clock/app-clock-events.model';
import { marketScheduleForToday } from '../components/app-clock/utils/clock.utils';
import { TradingTimeService } from './trading-time.service';

/**
 * How often the clock is advanced between server updates. A minute's resolution
 * is enough for market-session state and avoids portfolio-wide revaluation on the
 * per-second path used by the header clock.
 */
const TICK_INTERVAL_MS = 60_000;

/**
 * Whether the market is currently trading — the one thing خودکار needs in order
 * to decide which price the portfolio is measured at.
 *
 * Reads server time off the event bus rather than opening the `clock` SSE topic
 * itself. The header holds that connection for the life of the app and
 * publishes each time it receives; listening instead of connecting keeps this
 * service — and everything that reads it, the portfolio store included — clear
 * of the SSE and auth stack.
 *
 * Server time rather than the device's, deliberately: the basis decides what
 * every figure in the portfolio is worth, and a trader whose phone clock is an
 * hour out would otherwise read the market as open while it is shut. Until the
 * first server time arrives the device clock stands in — a basis that is
 * probably right beats a portfolio that renders nothing — and the first one
 * corrects it.
 */
@Service()
export class MarketSessionService {
	private readonly eventBus = inject(EventBusService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly tradingTimeService = inject(TradingTimeService);

	/** Last time the server reported, and the baseline the local ticker runs from. */
	private readonly serverTime = signal(new Date());
	private readonly currentTime = signal(new Date());

	/** True while any market group is in its scheduled trading period today. */
	readonly isOpen = computed(() =>
		this.tradingTimeService
			.marketGroups()
			.some(group => marketScheduleForToday(group, this.currentTime()).state === 'trading')
	);

	constructor() {
		this.eventBus
			.on$<ClockTimeEvent>(CLOCK_EVENTS.TIME)
			.pipe(
				map(event => event?.data),
				filter((time): time is number => typeof time === 'number' && Number.isFinite(time)),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(time => {
				this.serverTime.set(new Date(time));
				this.currentTime.set(new Date(time));
			});

		// Carries the clock forward between server updates, restarting from each
		// new server time so the local ticker can never drift away from it.
		toObservable(this.serverTime)
			.pipe(
				switchMap(baseline =>
					interval(TICK_INTERVAL_MS).pipe(
						map(tick => new Date(baseline.getTime() + (tick + 1) * TICK_INTERVAL_MS))
					)
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(time => this.currentTime.set(time));
	}
}
